import { useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { FaArrowLeft, FaChevronDown, FaChevronRight, FaGlobe, FaLinkedin, FaSync } from "react-icons/fa"
import { FaXTwitter } from "react-icons/fa6"
import { Line, LineChart, Tooltip, XAxis, YAxis, ResponsiveContainer } from "recharts"
import {
  Box,
  HStack,
  Icon,
  IconButton,
  Popover,
  Portal,
  Spinner,
  Stack,
  Table,
} from "@chakra-ui/react"
import { Button, Section, Text, toaster } from "@/components"
import { usePitchbookCompany } from "@/hooks"
import { syncPitchbookCompany } from "@/api"
import { PbDealData, PbDealInvestorData, PbEmployeeHistory } from "@/types"

const formatCurrency = (amount: number | null, currency: string | null) => {
  if (amount == null) return "—"
  const curr = currency || "USD"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: curr,
    maximumFractionDigits: 0,
  }).format(amount)
}

const parseSicCodes = (raw: string | null): string[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return parsed.map((item: { description: string }) => item.description)
  } catch {
    return [raw]
  }
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "—"
  return dateStr
}

const HeaderStat = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <Stack gap={0}>
    <Text fontSize="xs" color="whiteAlpha.700" fontWeight="medium">
      {label}
    </Text>
    <Text fontSize="sm" color="white" fontWeight="semibold">
      {value || "—"}
    </Text>
  </Stack>
)

const InfoCard = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <Box bg="gray.50" borderRadius="md" p={3} minW="160px">
    <Text fontSize="xs" color="gray.500" fontWeight="medium">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="semibold" mt={1}>
      {value || "—"}
    </Text>
  </Box>
)

const SynopsisPopover = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false)
  return (
    <Popover.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Popover.Trigger asChild>
        <Text
          fontSize="xs"
          lineClamp={2}
          cursor="default"
          _hover={{ color: "blue.600" }}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {text}
        </Text>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content maxW="400px" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
            <Popover.Arrow />
            <Popover.Body>
              <Text fontSize="xs" lineHeight="tall">{text}</Text>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

const DealRow = ({ deal, newInvestorIds }: { deal: PbDealData; newInvestorIds: Set<string> }) => {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const investors = deal.investors.filter((i) => i.role === "investor")
  const hasInvestors = investors.length > 0

  return (
    <>
      <Table.Row
        cursor={hasInvestors ? "pointer" : "default"}
        onClick={() => hasInvestors && setIsExpanded(!isExpanded)}
        _hover={{ bg: "gray.50" }}
      >
        <Table.Cell>
          {hasInvestors && (
            <Icon as={isExpanded ? FaChevronDown : FaChevronRight} boxSize={3} color="gray.400" />
          )}
        </Table.Cell>
        <Table.Cell>{formatDate(deal.dealDate)}</Table.Cell>
        <Table.Cell>{deal.dealType2Description || deal.dealType1Description || "—"}</Table.Cell>
        <Table.Cell>{formatCurrency(deal.dealSizeAmount, deal.dealSizeCurrency)}</Table.Cell>
        <Table.Cell>{formatCurrency(deal.raisedToDateAmount, deal.raisedToDateCurrency)}</Table.Cell>
        <Table.Cell>{deal.vcRound || "—"}</Table.Cell>
        <Table.Cell>{deal.vcRoundUpDownFlat || "—"}</Table.Cell>
        <Table.Cell>{deal.dealStatusDescription || "—"}</Table.Cell>
        <Table.Cell maxW="300px">
          {deal.dealSynopsis ? (
            <SynopsisPopover text={deal.dealSynopsis} />
          ) : (
            <Text fontSize="xs">—</Text>
          )}
        </Table.Cell>
      </Table.Row>
      {isExpanded && investors.map((inv: PbDealInvestorData) => {
        const isNew = newInvestorIds.has(`${deal.pbDealId}:${inv.pbInvestorId}`)
        return (
          <Table.Row key={inv.id} bg={isNew ? "#E8F0F8" : "blue.50"}>
            <Table.Cell />
            <Table.Cell colSpan={2}>
              <HStack gap={2}>
                <Text
                  fontSize="xs"
                  fontWeight="medium"
                  color="blue.700"
                  cursor="pointer"
                  _hover={{ textDecoration: "underline" }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    navigate(`/core/pitchbook/investor/${inv.pbInvestorId}`)
                  }}
                >
                  {inv.investorName || "Unknown Investor"}
                  {inv.leadSoleInvestor && " (Lead)"}
                </Text>
                {isNew && (
                  <Box bg="#D6E4F0" px={1.5} py={0.5} borderRadius="full">
                    <Text fontSize="2xs" color="#1C5180" fontWeight="bold">New Investor</Text>
                  </Box>
                )}
              </HStack>
            </Table.Cell>
            <Table.Cell>
              <Text fontSize="xs">{formatCurrency(inv.investmentAmountAmount, inv.investmentAmountCurrency)}</Text>
            </Table.Cell>
            <Table.Cell colSpan={2}>
              <Text fontSize="xs" color="gray.600">{inv.investmentStatus || ""}</Text>
            </Table.Cell>
            <Table.Cell colSpan={3}>
              <Text fontSize="xs" color="gray.600">{inv.leadPartnerName ? `Partner: ${inv.leadPartnerName}` : ""}</Text>
            </Table.Cell>
          </Table.Row>
        )
      })}
    </>
  )
}

const EmployeeHistoryChart = ({ data }: { data: PbEmployeeHistory[] }) => {
  const chartData = useMemo(() => {
    return [...data]
      .sort((a, b) => new Date(a.asOfDate).getTime() - new Date(b.asOfDate).getTime())
      .map((entry) => ({
        date: entry.asOfDate,
        employees: entry.employeeCount,
      }))
  }, [data])

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} bg="card.bg">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <XAxis dataKey="date" fontSize={12} />
          <YAxis allowDecimals={false} fontSize={12} />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="employees"
            name="Employees"
            stroke="#1C5180"
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}

export const PitchbookComponent = () => {
  const params = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const companyId = Number(params.companyId)
  const { data, isLoading } = usePitchbookCompany(companyId)
  const [isSyncing, setIsSyncing] = useState(false)

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      const result = await syncPitchbookCompany(companyId)
      await queryClient.invalidateQueries({ queryKey: ["pitchbook", companyId] })
      const parts = []
      if (result.profileUpdated) parts.push("profile")
      if (result.dealsProcessed > 0) parts.push(`${result.dealsProcessed} deals`)
      if (result.investorsProcessed > 0) parts.push(`${result.investorsProcessed} investors`)
      toaster.create({
        title: "PitchBook sync complete",
        description: parts.length > 0 ? `Updated: ${parts.join(", ")}` : "No new data",
        type: "success",
        duration: 5000,
      })
    } catch (err: any) {
      toaster.create({
        title: "Sync failed",
        description: err.message || "Failed to sync PitchBook data",
        type: "error",
        duration: 5000,
      })
    } finally {
      setIsSyncing(false)
    }
  }

  // Compute which investors are "new" per deal (first appearance chronologically)
  const newInvestorIds = useMemo(() => {
    if (!data) return new Set<string>()
    const seen = new Set<string>()
    const result = new Set<string>()
    // Deals are sorted desc by date — iterate oldest first
    const chronological = [...data.deals].reverse()
    for (const deal of chronological) {
      const investors = deal.investors.filter((i) => i.role === "investor")
      for (const inv of investors) {
        if (!seen.has(inv.pbInvestorId)) {
          seen.add(inv.pbInvestorId)
          result.add(`${deal.pbDealId}:${inv.pbInvestorId}`)
        }
      }
    }
    return result
  }, [data])

  if (isLoading) {
    return (
      <Section h="100vh" alignItems="center" justifyContent="center">
        <Spinner size="lg" />
      </Section>
    )
  }

  if (!data) {
    return (
      <Section h="100vh">
        <Text>No PitchBook data available for this company.</Text>
      </Section>
    )
  }

  const { profile, deals, employeeHistory } = data
  const hqLocation = [profile?.hqCity, profile?.hqStateProvince, profile?.hqCountry]
    .filter(Boolean)
    .join(", ")

  return (
    <Section overflowY="auto" h="100vh" p={0} gap={0}>
      {/* Back Button */}
      <Box px={4} py={2}>
        <IconButton
          aria-label="Back to company"
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/core/company/${companyId}`)}
        >
          <FaArrowLeft />
        </IconButton>
      </Box>

      {/* Header */}
      <Box bg="#1C5180" color="white" px={8} py={6}>
        <HStack justify="space-between" align="flex-start">
          <Stack gap={1}>
            <Text fontSize="2xl" fontWeight="bold" color="white">
              {profile?.formalName || profile?.alsoKnownAs || "Company"}
            </Text>
            {profile?.legalName && (
              <Text fontSize="sm" color="whiteAlpha.800">
                {profile.legalName}
              </Text>
            )}
            <HStack gap={2} mt={1}>
              {profile?.ticker && (
                <Box bg="whiteAlpha.200" px={2} py={0.5} borderRadius="sm">
                  <Text fontSize="xs" color="white" fontWeight="bold">
                    {profile.exchange ? `${profile.exchange}: ${profile.ticker}` : profile.ticker}
                  </Text>
                </Box>
              )}
            </HStack>
          </Stack>
          <HStack gap={3}>
            <Button
              size="sm"
              variant="outline"
              color="white"
              borderColor="whiteAlpha.400"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={handleSync}
              loading={isSyncing}
            >
              <FaSync />
              Sync from PitchBook
            </Button>
            {profile?.website && (
              <IconButton
                aria-label="Website"
                variant="ghost"
                size="sm"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
                onClick={() => window.open(profile.website!.startsWith("http") ? profile.website! : `https://${profile.website}`, "_blank")}
              >
                <FaGlobe />
              </IconButton>
            )}
            {profile?.linkedInUrl && (
              <IconButton
                aria-label="LinkedIn"
                variant="ghost"
                size="sm"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
                onClick={() => window.open(profile.linkedInUrl!, "_blank")}
              >
                <FaLinkedin />
              </IconButton>
            )}
            {profile?.twitterUrl && (
              <IconButton
                aria-label="Twitter"
                variant="ghost"
                size="sm"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
                onClick={() => window.open(profile.twitterUrl!, "_blank")}
              >
                <FaXTwitter />
              </IconButton>
            )}
          </HStack>
        </HStack>

        <HStack gap={8} mt={4} flexWrap="wrap">
          <HeaderStat label="Year Founded" value={profile?.yearFounded?.toString()} />
          <HeaderStat label="Employees" value={profile?.employees?.toLocaleString()} />
          <HeaderStat label="HQ Location" value={hqLocation || null} />
          <HeaderStat label="Ownership Status" value={profile?.ownershipStatusDescription} />
          <HeaderStat label="Financing Status" value={profile?.financingStatusDescription} />
        </HStack>
      </Box>

      {/* Overview */}
      <Stack px={8} py={6} gap={5}>
        {profile?.description && (
          <Stack gap={1}>
            <Text fontSize="md" fontWeight="bold">
              Overview
            </Text>
            <Text fontSize="sm" color="gray.700" lineHeight="tall">
              {profile.description}
            </Text>
          </Stack>
        )}

        <HStack gap={3} flexWrap="wrap">
          <InfoCard label="Business Status" value={profile?.businessStatusDescription} />
          <InfoCard label="Financing Status" value={profile?.financingStatusDescription} />
          <InfoCard label="Universe" value={profile?.universeDescription} />
          <InfoCard
            label="Total Raised"
            value={
              profile?.totalMoneyRaised != null
                ? `${formatCurrency(profile.totalMoneyRaised, profile.totalMoneyRaisedCurrency)}${profile.totalMoneyRaisedEstimated ? " (est.)" : ""}`
                : null
            }
          />
          <InfoCard label="Morningstar Code" value={profile?.morningstarCode} />
          <Box bg="gray.50" borderRadius="md" p={3} minW="160px">
            <Text fontSize="xs" color="gray.500" fontWeight="medium">SIC Codes</Text>
            <HStack gap={1} flexWrap="wrap" mt={1}>
              {parseSicCodes(profile?.sicCodes ?? null).length > 0
                ? parseSicCodes(profile?.sicCodes ?? null).map((desc) => (
                    <Box key={desc} bg="gray.200" px={1.5} py={0.5} borderRadius="sm">
                      <Text fontSize="xs" fontWeight="medium">{desc}</Text>
                    </Box>
                  ))
                : <Text fontSize="sm" fontWeight="semibold">—</Text>
              }
            </HStack>
          </Box>
        </HStack>

        {profile?.parentCompanyName && (
          <HStack gap={3}>
            <InfoCard label="Parent Company" value={profile.parentCompanyName} />
          </HStack>
        )}

        {/* Financing History */}
        {deals.length > 0 && (
          <Stack gap={2} mt={2}>
            <Text fontSize="md" fontWeight="bold">
              Financing History
            </Text>
            <Box borderWidth="1px" borderRadius="md" overflow="auto">
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row bg="gray.50">
                    <Table.ColumnHeader w="30px" />
                    <Table.ColumnHeader>Date</Table.ColumnHeader>
                    <Table.ColumnHeader>Deal Type</Table.ColumnHeader>
                    <Table.ColumnHeader>Deal Size</Table.ColumnHeader>
                    <Table.ColumnHeader>Raised to Date</Table.ColumnHeader>
                    <Table.ColumnHeader>VC Round</Table.ColumnHeader>
                    <Table.ColumnHeader>Up/Down/Flat</Table.ColumnHeader>
                    <Table.ColumnHeader>Status</Table.ColumnHeader>
                    <Table.ColumnHeader>Synopsis</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {deals.map((deal) => (
                    <DealRow key={deal.id} deal={deal} newInvestorIds={newInvestorIds} />
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Stack>
        )}

        {/* Employee History Chart */}
        {employeeHistory.length > 0 && (
          <Stack gap={2} mt={2}>
            <Text fontSize="md" fontWeight="bold">
              Employee History
            </Text>
            <EmployeeHistoryChart data={employeeHistory} />
          </Stack>
        )}
      </Stack>
    </Section>
  )
}
